import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Container,
  Typography,
  Button,
  Box,
  Divider,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { NavLink as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import useResponsive from '../hooks/useResponsive';
import Logo from '../components/logo';
import Iconify from '../components/iconify';
import axios from '../utils/Axios';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import FormProvider, { RHFTextField } from '../components/hook-form';
import { useAuthContext } from '../auth/useAuthContext';
import { themeEventEmitter } from 'src/theme';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(() => ({
  width: '50%',
  padding: 50,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: localStorage.getItem('primaryColor') || '#46C492',
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

const urlSearchParams = new URLSearchParams(window.location.search);
const error = urlSearchParams.get('error');
const price = urlSearchParams.get('price') || '';

export default function LoginPage() {
  const { loginUserWithPassword } = useAuthContext();
  const settings = {
    dots: true,
    autoplay: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const methods = useForm({ defaultValues: { email: '', password: '' } });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add explicit loading state

  const [errorForm, setErrorForm] = useState(null);

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const mdUp = useResponsive('up', 'sm');

  const handleLoginWithGoogle = async () => {
    try {
      setIsLoading(true); // Set loading state
      const response = await axios.get(`/auth/google/url?price=${price}`);
      if (response.data) {
        window.location = response.data.url;
      }
    } catch (error) {
      console.error("Google login error:", error);
      setErrorForm({ message: "Failed to initiate Google login" });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true); // Set loading state
      await loginUserWithPassword(data);
      window.location = `/verify?state=${price}`;
    } catch (error) {
      reset();
      const errorMessage = error.messages ? error.messages.join('\n') : 
                          (error.message || 'Login failed. Please try again.');
      setErrorForm({ message: errorMessage });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <>
      <Helmet>
        <title> Login | {localStorage.getItem("companyName") ?? "FlowBookings"} </title>
      </Helmet>

      <StyledRoot>
        <Logo
          width="160px"
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />

        <Container>
          <StyledContent>
            <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
              Welcome to {localStorage.getItem("companyName") ?? "FlowBookings"}!
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}
            >
              Enter your details to get started.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Login failed please make sure you have the social email
                connected in your settings
              </Alert>
            )}
            {window.location.hostname.includes("app.flowbookings.io") && (
              <Button
                fullWidth
                size="large"
                color="inherit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  backgroundColor: '#4285f4',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#4285f4',
                  },
                }}
                onClick={handleLoginWithGoogle}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Iconify
                    icon="eva:google-fill"
                    color="#fff"
                    width={22}
                    height={22}
                  />
                )}

                <Typography sx={{ px: 2 }}>
                  {isLoading ? 'Processing...' : 'Login With Google'}
                </Typography>
              </Button>
            )}
            {window.location.hostname.includes("app.flowbookings.io") && (
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  or continue with email
                </Typography>
              </Divider>
            )}
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              {!!errorForm && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorForm.message}
                </Alert>
              )}
              <RHFTextField
                name="email"
                size="large"
                placeholder="Email"
                InputProps={{
                  sx: { mb: 3 },
                }}
              />
              <RHFTextField
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                size="large"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        <Iconify
                          icon={
                            showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                component={RouterLink}
                to="/reset-request"
                variant="body2"
                sx={{
                  color: localStorage.getItem("primaryColor") ?? '#46C492',
                  float: 'right',
                  fontWeight: 'bold',
                  mt: 2,
                  mb: 2,
                }}
              >
                Forgot Password?
              </Typography>
              <LoadingButton
  type="submit"
  fullWidth
  size="large"
  variant="contained"
  loading={isLoading || isSubmitting}
  loadingIndicator={
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
      Processing...
    </Box>
  }
  sx={{ 
    backgroundColor: localStorage.getItem("primaryColor") ?? '#36B37E',
  }}
>
  Log in
</LoadingButton>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ justifyContent: 'center', mt: 2 }}
              >
                <Typography variant="body2">Don't have an account?</Typography>
                <Typography
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  sx={{ color: localStorage.getItem("primaryColor") ?? '#46C492', fontWeight: 'bold' }}
                >
                  Create an account
                </Typography>
              </Stack>
            </FormProvider>
          </StyledContent>
        </Container>

        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ mb: 3, color: '#fff' }}>
              The Ultimate Booking Solution <br /> for Webflow!
            </Typography>

            <Slider {...settings}>
              <Box
                component="img"
                src="/assets/images/products/Cloneable_7.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_10.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_12.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
              <Box
                component="img"
                src="/assets/images/products/Cloneable_14.png"
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                }}
              />
            </Slider>
          </StyledSection>
        )}
      </StyledRoot>
    </>
  );
}
